import {call} from 'redux-saga/effects'

import {inheritDescriptor} from './inheritDescriptor'

type Gen<R = any> = Generator<any, R, any>

type Saga<TArgs extends any[] = any[], TRes = any> = (...args: TArgs) => Gen<TRes>

type PipeMeta = {
  results: any[]
  levels: number
  errorLevel: number
}

export const enum Invoke {
  After = 'after',
  Before = 'before',
}

export const pipe =
  (saga: Saga, invoke: Invoke = Invoke.Before, saveResult = true) =>
  (target: any, key: string, descriptor: PropertyDescriptor): PropertyDescriptor => {
    const origin = descriptor.value as Saga & {
      __$pipeMeta: PipeMeta
    }

    origin.__$pipeMeta = origin.__$pipeMeta || {
      results: [],
      levels: 0,
      errorLevel: Infinity,
    }

    origin.__$pipeMeta.levels++
    const currentLevel = origin.__$pipeMeta.levels

    const cleanupMeta = () => {
      const {results} = origin.__$pipeMeta
      results.splice(0)
      origin.__$pipeMeta.errorLevel = Infinity
    }

    const invokeWithArgs = function* (saga: Saga & {__$ignoreResults?: boolean}, ...originArgs: any[]): any {
      const {results} = origin.__$pipeMeta
      const args: any[] = saga.__$ignoreResults ? originArgs : [...originArgs, ...results]
      return yield call([this, saga], ...args)
    }

    const executeBefore = Object.assign(
      function* (...args: any[]) {
        let length: number | undefined
        try {
          const pipeResult: any = yield call([this, invokeWithArgs], saga, ...args)
          if (saveResult) {
            length = origin.__$pipeMeta.results.push(pipeResult)
          }

          const result = yield call([this, invokeWithArgs], origin, ...args)
          origin.__$pipeMeta.errorLevel = Infinity

          return result
        } catch (e) {
          origin.__$pipeMeta.errorLevel = currentLevel
          throw e
        } finally {
          if (currentLevel === origin.__$pipeMeta.levels) {
            cleanupMeta()
          } else if (currentLevel >= origin.__$pipeMeta.errorLevel && length) {
            origin.__$pipeMeta.results.splice(length - 1)
          }
        }
      },
      {
        __$ignoreResults: true,
      },
    )

    const executeAfter = Object.assign(
      function* (...args: any[]) {
        try {
          const {results} = origin.__$pipeMeta

          const originResult: any = yield call([this, invokeWithArgs], origin, ...args)
          results.push(originResult)

          const sagaResult = yield call([this, invokeWithArgs], saga, ...args)
          if (saveResult) {
            return sagaResult
          }

          results.splice(results.length - 1, 1)
          return originResult
        } finally {
          if (currentLevel === origin.__$pipeMeta.levels) {
            cleanupMeta()
          }
        }
      },
      {
        __$ignoreResults: true,
      },
    )

    return inheritDescriptor(descriptor, invoke === Invoke.After ? executeAfter : executeBefore)
  }
